import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BasicModal from '../../BasicModal/BasicModal';
import ProductField from '../Transaction/Fields/ProductField';
import DividerField from '../Transaction/Fields/DividerField';
import DetailRow, {
  TransactionDetailRow,
  TransactionDetailLabel,
  TransactionDetailValue,
} from '../Transaction/Fields/DetailRow';
import Status from '../../Portfolio/RecentTransactions/Status';
import ModalButtons from '../../Modal/ModalButtons';
import { ReactComponent as DollarRoundIcon } from '../../../assets/images/svg/dollar-round.svg';
import { triggerOrderStatusTypesArray } from '../../../util/enum/api/transactionTypes';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { formatDate } from '../../../util/helpers/dateHelpers';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { uFlexCenter } from '../../../assets/styles/utility';
import { pxToRem } from '../../../assets/styles/helper';

const TriggerOrderIconWrap = styled.div`
  ${uFlexCenter};
  margin-top: ${pxToRem(-8)};
  margin-bottom: ${pxToRem(16)};

  svg {
    width: ${pxToRem(40)};
    height: ${pxToRem(40)};
  }
`;

const CloseButtonWrap = styled.div`
  margin-top: auto;
  padding: 0 ${pxToRem(16)} ${pxToRem(16)};
`;

const TriggerOrderDetailsModal = ({ isOpen, onClose, transaction }) => {
  const { t } = useTranslation();
  const brokerSettings = useSelector(selectSettings);

  if (isEmpty(transaction)) {
    return null;
  }

  const unit = t(
    transaction.Quantity > 1
      ? 'portfolio.transactions.details.pluralUnit'
      : 'portfolio.transactions.details.singleUnit',
  );

  // Only present once a trigger order has a historical transaction record
  // behind it (transaction.Fields comes from the /transactions list, already
  // normalized by fetchPortfolioTransactions) — a still-pending trigger order
  // has no Fields array yet, so this stays absent until then.
  const targetPriceIdField = transaction.Fields?.find(
    (field) => field.CaptionType === 'TargetPriceId',
  );

  return (
    <BasicModal
      size="md"
      isOpen={isOpen}
      close={onClose}
      title={t('portfolio.pendingTransactions.details.title')}
      dataCy="container-trigger-order-details"
      closeIconVisible
      fixedHeight
    >
      <TriggerOrderIconWrap>
        <DollarRoundIcon />
      </TriggerOrderIconWrap>
      <ProductField
        field={transaction}
        isSegregated={transaction.IsSegregated}
        segregatedCaption={brokerSettings?.SegregatedCaption}
      />
      <DividerField />
      <TransactionDetailRow>
        <TransactionDetailLabel>
          {t('portfolio.pendingTransactions.details.summaryLabel')}
        </TransactionDetailLabel>
        <TransactionDetailValue>
          {t('portfolio.pendingTransactions.details.summary', {
            quantity: transaction.Quantity,
            unit,
          })}
        </TransactionDetailValue>
      </TransactionDetailRow>
      <TransactionDetailRow>
        <TransactionDetailLabel>
          {t('portfolio.pendingTransactions.tableHeadings.status')}
        </TransactionDetailLabel>
        <TransactionDetailValue>
          <Status
            status={parseEnumType(
              triggerOrderStatusTypesArray,
              transaction.TriggerOrderStatus,
            )}
          />
        </TransactionDetailValue>
      </TransactionDetailRow>
      <TransactionDetailRow>
        <TransactionDetailLabel>
          {t('portfolio.pendingTransactions.details.submittedOn')}
        </TransactionDetailLabel>
        <TransactionDetailValue>
          {formatDate(transaction.RequestDate, 'MMMM d, y hh:mm aa')}
        </TransactionDetailValue>
      </TransactionDetailRow>
      <TransactionDetailRow>
        <TransactionDetailLabel>
          {t('portfolio.pendingTransactions.details.submitter')}
        </TransactionDetailLabel>
        <TransactionDetailValue>
          {transaction.SubmitterUid}
        </TransactionDetailValue>
      </TransactionDetailRow>
      {targetPriceIdField && (
        <DetailRow field={targetPriceIdField} brokerSettings={brokerSettings} />
      )}
      <CloseButtonWrap>
        <ModalButtons
          isVertical
          showOnlySecondary
          secondaryButtonProps={{
            onClick: onClose,
            label: t('common.close'),
          }}
        />
      </CloseButtonWrap>
    </BasicModal>
  );
};

TriggerOrderDetailsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  transaction: PropTypes.shape({
    ProductCaption: PropTypes.string,
    LocationType: PropTypes.number,
    IsSegregated: PropTypes.bool,
    SubmitterUid: PropTypes.string,
    Quantity: PropTypes.number,
    TriggerOrderStatus: PropTypes.number,
    RequestDate: PropTypes.string,
    Fields: PropTypes.arrayOf(
      PropTypes.shape({
        CaptionType: PropTypes.string,
      }),
    ),
  }),
};

export default TriggerOrderDetailsModal;
